import { useState } from 'react';
import { useI18n } from '../../i18n';
import { useSEO } from '../../hooks/useSEO';

export default function MarkdownPreview() {
    const { t } = useI18n();
    const [markdown, setMarkdown] = useState(`# Welcome to Markdown Preview

## Features

This tool supports **GitHub Flavored Markdown** including:

- **Bold** and *italic* text
- [Links](https://example.com)
- \`Inline code\`
- Lists (ordered and unordered)
- Tables
- Code blocks
- Blockquotes
- And more!

### Code Example

\`\`\`javascript
function hello(name) {
    console.log(\`Hello, \${name}!\`);
}
\`\`\`

### Table Example

| Feature | Supported |
|---------|-----------|
| Headers | ✅ |
| Lists | ✅ |
| Code | ✅ |
| Tables | ✅ |

> **Note**: This is a blockquote with some important information.

---

Try editing the markdown on the left to see the preview update in real-time!
`);

    useSEO({
        title: t.tools.markdown.name,
        description: t.tools.markdown.description,
        keywords: ['markdown', 'preview', 'editor', 'GFM', 'GitHub Flavored Markdown'],
    });

    // Simple markdown parser (basic implementation)
    const parseMarkdown = (text: string): string => {
        let html = text;

        // Escape HTML
        html = html.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

        // Code blocks
        html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (_, lang, code) => {
            return `<pre class="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4"><code class="language-${lang || 'plaintext'}">${code.trim()}</code></pre>`;
        });

        // Headers
        html = html.replace(/^### (.*$)/gm, '<h3 class="text-xl font-bold text-gray-900 mt-6 mb-3">$1</h3>');
        html = html.replace(/^## (.*$)/gm, '<h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">$1</h2>');
        html = html.replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold text-gray-900 mt-8 mb-4">$1</h1>');

        // Bold and Italic
        html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
        html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>');
        html = html.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');

        // Inline code
        html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');

        // Links
        html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary-600 hover:text-primary-700 underline" target="_blank" rel="noopener noreferrer">$1</a>');

        // Horizontal rule
        html = html.replace(/^---$/gm, '<hr class="my-8 border-t border-gray-300" />');

        // Blockquotes
        html = html.replace(/^&gt; (.*)$/gm, '<blockquote class="border-l-4 border-primary-500 pl-4 py-2 my-4 bg-primary-50 text-gray-700">$1</blockquote>');

        // Tables
        html = html.replace(/^\|(.+)\|$/gm, (match) => {
            const cells = match.split('|').filter(c => c.trim());
            if (cells.some(c => c.match(/^[\s:-]+$/))) {
                return ''; // Skip separator row
            }
            const isHeader = !html.substring(0, html.indexOf(match)).includes('<table');
            const tag = isHeader ? 'th' : 'td';
            const cellClass = isHeader ?
                'px-4 py-2 bg-gray-100 font-semibold text-left border border-gray-300' :
                'px-4 py-2 border border-gray-300';

            const row = cells.map(c => `<${tag} class="${cellClass}">${c.trim()}</${tag}>`).join('');

            if (isHeader) {
                return `<table class="min-w-full border-collapse my-4"><thead><tr>${row}</tr></thead><tbody>`;
            }
            return `<tr>${row}</tr>`;
        });

        // Close table at end or before non-table content
        if (html.includes('<table')) {
            html = html.replace(/(<\/tr>)(?![\s\S]*<tr)/g, '$1</tbody></table>');
        }

        // Unordered lists
        html = html.replace(/^- (.*)$/gm, '<li class="ml-6 list-disc">$1</li>');
        html = html.replace(/(<li class="ml-6 list-disc">.*<\/li>)/s, '<ul class="my-4">$1</ul>');

        // Ordered lists  
        html = html.replace(/^\d+\. (.*)$/gm, '<li class="ml-6 list-decimal">$1</li>');
        html = html.replace(/(<li class="ml-6 list-decimal">.*<\/li>)/s, '<ol class="my-4">$1</ol>');

        // Paragraphs
        html = html.split('\n\n').map(para => {
            if (para.trim() &&
                !para.match(/^<(h[1-6]|ul|ol|pre|blockquote|table|hr)/) &&
                !para.match(/<\/(h[1-6]|ul|ol|pre|blockquote|table|hr)>$/)) {
                return `<p class="my-4 leading-relaxed">${para}</p>`;
            }
            return para;
        }).join('\n\n');

        // Line breaks
        html = html.replace(/\n/g, '<br />');

        return html;
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {t.tools.markdown.name}
                </h1>
                <p className="text-gray-600">{t.tools.markdown.description}</p>
                <p className="text-sm text-gray-500 mt-1">
                    {t.tools.markdown.supportedFeatures}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Editor */}
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        {t.tools.markdown.editor}
                    </h2>
                    <textarea
                        value={markdown}
                        onChange={(e) => setMarkdown(e.target.value)}
                        className="input-field font-mono text-sm h-[600px] resize-none"
                        placeholder="Enter your markdown here..."
                    />
                </div>

                {/* Preview */}
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        {t.tools.markdown.preview}
                    </h2>
                    <div
                        className="bg-white border border-gray-200 rounded-lg p-6 h-[600px] overflow-y-auto prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(markdown) }}
                    />
                </div>
            </div>

            {/* Info */}
            <div className="mt-6 bg-primary-50 border border-primary-200 rounded-lg p-4">
                <h3 className="font-semibold text-primary-900 mb-2">
                    💡 Markdown Quick Reference
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-primary-800">
                    <div className="space-y-1">
                        <p><code className="bg-white px-1 rounded"># Heading 1</code></p>
                        <p><code className="bg-white px-1 rounded">## Heading 2</code></p>
                        <p><code className="bg-white px-1 rounded">**bold**</code></p>
                        <p><code className="bg-white px-1 rounded">*italic*</code></p>
                        <p><code className="bg-white px-1 rounded">`code`</code></p>
                    </div>
                    <div className="space-y-1">
                        <p><code className="bg-white px-1 rounded">[Link](url)</code></p>
                        <p><code className="bg-white px-1 rounded">- List item</code></p>
                        <p><code className="bg-white px-1 rounded">&gt; Blockquote</code></p>
                        <p><code className="bg-white px-1 rounded">```code block```</code></p>
                        <p><code className="bg-white px-1 rounded">---</code> Horizontal rule</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
